// A/B History — read-only ledger of every concluded A/B test.
//
// Each row is a snapshot taken at "Promote winner" time: it captures both
// variants' final metrics, the significance verdict, the split ratio, and
// the actual cadence steps that were being tested. This is the page users
// come to when they want to answer "what actually converts?".
import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Trophy, TrendingUp, Filter, ChevronDown, ChevronRight, Zap, Calendar, GitCompare } from "lucide-react";

const fmtRate = (r) => (r == null ? "—" : `${(r * 100).toFixed(1)}%`);
const fmtLift = (r) => (r == null ? "—" : `${r >= 0 ? "+" : ""}${(r * 100).toFixed(0)}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};
const fmtDuration = (startIso, endIso) => {
  if (!startIso || !endIso) return "—";
  const ms = new Date(endIso) - new Date(startIso);
  if (ms <= 0) return "—";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  return hours >= 1 ? `${hours}h` : "<1h";
};

function Bookings({ v }) {
  const total = (v?.stages?.booked || 0) + (v?.stages?.demo || 0) + (v?.stages?.won || 0);
  return <span className="font-mono">{total}</span>;
}

function CadenceMini({ steps, testidPrefix }) {
  if (!steps || !steps.length) {
    return <div className="text-[10px] font-mono text-zinc-400 italic px-2">no cadence recorded</div>;
  }
  return (
    <div className="space-y-1" data-testid={testidPrefix}>
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2 text-[10px] font-mono text-zinc-700 px-2 py-1 bg-white border border-zinc-200">
          <span className="w-4 text-zinc-400">#{i + 1}</span>
          <span className="uppercase tracking-wider text-[9px] font-bold text-[#002FA7] w-9">
            {(s.kind || "").toUpperCase()}
          </span>
          <span className="text-zinc-500">
            d{s.delay_days ?? 0}·{String(s.hour ?? 0).padStart(2, "0")}:{String(s.minute ?? 0).padStart(2, "0")}
          </span>
          <span className="text-zinc-700 truncate">{s.angle || ""}</span>
        </div>
      ))}
    </div>
  );
}

function HistoryRow({ item, expanded, onToggle }) {
  const a = item.variants?.A || {};
  const b = item.variants?.B || {};
  const promoted = item.promoted_variant;
  const sig = item.significance?.bookings || {};
  const isSignificant = !!sig.significant_95;
  const promotedIsA = promoted === "A";

  return (
    <div className="border-b border-zinc-200 last:border-b-0" data-testid={`ab-history-row-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-zinc-50 text-left transition-colors"
        data-testid={`ab-history-toggle-${item.id}`}
      >
        <div className="col-span-3 flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-zinc-400" />}
          <span className="text-xs font-medium text-zinc-900 truncate" title={item.campaign_name}>
            {item.campaign_name || "(unnamed campaign)"}
          </span>
        </div>

        <div className="col-span-2 flex items-center gap-1.5 text-[11px] font-mono text-zinc-500">
          <Calendar className="w-3 h-3 shrink-0" />
          {fmtDate(item.promoted_at)}
          <span className="text-zinc-300">·</span>
          <span>{fmtDuration(item.started_at, item.promoted_at)}</span>
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${
            promotedIsA ? "border-[#002FA7] text-[#002FA7] bg-blue-50" : "border-amber-500 text-amber-800 bg-amber-50"
          }`}>
            <Trophy className="w-3 h-3 inline mr-0.5 -mt-0.5" />
            Promoted {promoted}
          </span>
          {isSignificant && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300" title="Statistically significant (p<0.05)">
              sig
            </span>
          )}
        </div>

        <div className="col-span-3 text-[11px] font-mono text-zinc-600 flex items-center gap-3">
          <span>
            <span className="text-[#002FA7] font-bold">A</span> · <Bookings v={a} /> cita · {fmtRate(a.booked_rate + a.demo_rate + a.won_rate)}
          </span>
          <span className="text-zinc-300">vs</span>
          <span>
            <span className="text-amber-700 font-bold">B</span> · <Bookings v={b} /> cita · {fmtRate(b.booked_rate + b.demo_rate + b.won_rate)}
          </span>
        </div>

        <div className="col-span-2 text-right flex items-center justify-end gap-2">
          <TrendingUp className={`w-3.5 h-3.5 ${isSignificant ? "text-emerald-600" : "text-zinc-400"}`} />
          <span className={`text-sm font-mono font-bold ${isSignificant ? "text-emerald-700" : "text-zinc-500"}`} data-testid={`ab-history-lift-${item.id}`}>
            {fmtLift(item.lift)}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="bg-zinc-50 border-t border-zinc-200 px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`ab-history-detail-${item.id}`}>
          {/* Variant metrics side-by-side */}
          {["A", "B"].map((label) => {
            const v = item.variants?.[label] || {};
            const isPromoted = promoted === label;
            return (
              <div key={label} className={`border p-3 ${isPromoted ? (label === "A" ? "border-[#002FA7]" : "border-amber-500") : "border-zinc-200"} bg-white`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${label === "A" ? "text-[#002FA7]" : "text-amber-700"}`}>
                    Variant {label} {label === "A" ? "· control" : "· challenger"}
                  </div>
                  {isPromoted && (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                      label === "A" ? "bg-blue-100 text-[#002FA7]" : "bg-amber-100 text-amber-800"
                    }`}>
                      <Zap className="w-2.5 h-2.5 inline -mt-0.5" /> promoted
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-1 text-[10px] font-mono">
                  <Stat label="Leads" val={v.leads_assigned} />
                  <Stat label="Contacted" val={v.leads_contacted} />
                  <Stat label="Booked" val={v.stages?.booked} />
                  <Stat label="Demo" val={v.stages?.demo} />
                  <Stat label="Won" val={v.stages?.won} />
                  <Stat label="Resp." val={fmtRate(v.response_rate)} />
                  <Stat label="Book rate" val={fmtRate(v.booked_rate)} />
                  <Stat label="Won rate" val={fmtRate(v.won_rate)} />
                </div>

                <div className="mt-3">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
                    <GitCompare className="w-3 h-3" /> Cadence
                  </div>
                  <CadenceMini
                    steps={label === "A" ? item.variant_a_steps : item.variant_b_steps}
                    testidPrefix={`ab-history-cadence-${label.toLowerCase()}-${item.id}`}
                  />
                </div>
              </div>
            );
          })}

          {/* Significance footer */}
          <div className="md:col-span-2 border border-zinc-200 bg-white p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">
              Statistical verdict
            </div>
            <div className="text-[11px] font-mono text-zinc-700 flex flex-wrap gap-4">
              <span>z = <strong>{sig.z ?? "—"}</strong></span>
              <span>p = <strong>{sig.p_value ?? "—"}</strong></span>
              <span>Split ratio: <strong>{Math.round((item.ab_split_ratio ?? 0.5) * 100)}% A / {100 - Math.round((item.ab_split_ratio ?? 0.5) * 100)}% B</strong></span>
              <span className={isSignificant ? "text-emerald-700 font-bold" : "text-amber-700"}>
                {isSignificant ? "Statistically significant (p<0.05)" : (sig.note || "Not statistically significant")}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, val }) {
  return (
    <div className="border border-zinc-100 px-1.5 py-1 text-center bg-zinc-50">
      <div className="text-[8px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-zinc-900 font-bold">{val == null ? "—" : val}</div>
    </div>
  );
}

export default function ABHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("");

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (opts.campaignId || campaignFilter) params.campaign_id = opts.campaignId ?? campaignFilter;
      const { data } = await api.get("/analytics/ab-history", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load A/B history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignFilter]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaigns(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const totals = useMemo(() => {
    const bySig = items.filter((i) => i.significance?.bookings?.significant_95).length;
    const avgLift = (() => {
      const lifts = items.map((i) => i.lift).filter((x) => typeof x === "number");
      if (!lifts.length) return null;
      return lifts.reduce((a, b) => a + b, 0) / lifts.length;
    })();
    return { total: items.length, significant: bySig, avgLift };
  }, [items]);

  return (
    <div className="p-8 max-w-[1400px]" data-testid="ab-history-page">
      <div className="border-b border-zinc-200 pb-6 mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">/ analytics · a/b history</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight flex items-center gap-3">
            A/B History
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Every A/B test you've concluded — with the winning variant, the lift, and the actual
            cadences that were being compared. Click any row to see the full breakdown and learn
            what patterns keep winning.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <Filter className="w-3.5 h-3.5 text-zinc-400" />
            <select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              data-testid="ab-history-campaign-filter"
              className="px-3 py-2 text-xs border border-zinc-300 bg-white focus:outline-none focus:border-[#002FA7]"
            >
              <option value="">All campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-8" data-testid="ab-history-kpis">
        <div className="border border-zinc-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Tests concluded</div>
          <div className="text-3xl font-mono font-black text-zinc-900 mt-1" data-testid="ab-history-total">{totals.total}</div>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Statistically significant</div>
          <div className="text-3xl font-mono font-black text-emerald-700 mt-1" data-testid="ab-history-sig">
            {totals.significant}
            <span className="text-sm text-zinc-400 ml-2">/ {totals.total}</span>
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Avg. winning lift</div>
          <div className="text-3xl font-mono font-black text-zinc-900 mt-1" data-testid="ab-history-avg-lift">
            {totals.avgLift == null ? "—" : fmtLift(totals.avgLift)}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-400" data-testid="ab-history-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading A/B history…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="ab-history-empty">
          <Trophy className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
          <div className="text-sm font-bold text-zinc-800 mb-1">No concluded A/B tests yet</div>
          <p className="text-xs text-zinc-500 max-w-md mx-auto">
            Enable A/B testing on a campaign, let leads flow, then promote a winner from the
            Campaign edit page. This ledger will fill in as you close each test.
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white" data-testid="ab-history-list">
          <div className="grid grid-cols-12 gap-3 items-center px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
            <div className="col-span-3">Campaign</div>
            <div className="col-span-2">Concluded · duration</div>
            <div className="col-span-2">Outcome</div>
            <div className="col-span-3">A vs B (cita · booking-rate)</div>
            <div className="col-span-2 text-right">Lift</div>
          </div>
          {items.map((item) => (
            <HistoryRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
